import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, Trash2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { errorCode } from "@/lib/api-errors";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type {
  AiBillingMode,
  CollectionMethod,
  BillingConfig,
  BillingInterval,
  CreditGrant,
  InstanceDetail,
  InstancePayment,
  NoInstanceLlmKeysError,
  PaymentsListResponse,
  UpsertBillingConfigRequest,
} from "@/types/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { formatDateTime } from "@/lib/utils";
import { formatMoney, formatPeriod } from "@/lib/billing-format";
import { InstanceEmployeesSection } from "@/routes/InstanceEmployeesSection";
import { InstanceAiSettingsSection } from "@/components/instance-ai-settings-section";
import { InstanceBillingOperationsSection } from "@/components/instance-billing-operations-section";
import { InstanceBillingAuditCard } from "@/components/instance-billing-audit-card";
import { InvoiceRowActions } from "@/components/billing-ops/invoice-row-actions";
import { CreditNoteAction } from "@/components/billing-ops/credit-note-dialog";
import { StripeCustomerEmailKV } from "@/components/billing-ops/stripe-customer-email-kv";
import { StripeCustomerCard } from "@/components/billing-ops/stripe-customer-card";
import { InstanceLlmUsageSection } from "@/components/instance-llm-usage-section";
import { ProvisioningStepsCard } from "@/components/provisioning-steps-card";
import { InstanceVoiceUsageSection } from "@/components/instance-voice-usage-section";

interface BillingFormState {
  amountMajor: string;
  grossMajor: string;
  currency: string;
  interval: BillingInterval;
  vatRatePercent: string;
  description: string;
  notes: string;
  isActive: boolean;
  aiBillingMode: AiBillingMode;
  monthlyAllowanceCredits: string;
  collectionMethod: CollectionMethod;
  invoiceDueDays: string;
}

const INTERVALS: BillingInterval[] = ["1 month", "3 months", "6 months", "12 months"];

const INTERVAL_LABEL_KEYS = {
  "1 month": "detail.billingConfig.intervals.month1",
  "3 months": "detail.billingConfig.intervals.months3",
  "6 months": "detail.billingConfig.intervals.months6",
  "12 months": "detail.billingConfig.intervals.months12",
} as const satisfies Record<BillingInterval, string>;

type BillingFormError =
  | "detail.billingConfig.errors.amount"
  | "detail.billingConfig.errors.vat"
  | "detail.billingConfig.errors.descriptionRequired"
  | "detail.billingConfig.errors.allowance"
  | "detail.billingConfig.errors.paymentTerm";

/**
 * Net is the canonical value stored as `amountCents` — Stripe uses it as the
 * VAT-exclusive `unit_amount` and adds VAT on top via tax rates at checkout.
 * Gross is always derived for the operator's convenience. Both helpers take
 * and return major-unit strings; a blank/non-numeric input yields "".
 */
function grossFromNet(netMajor: string, vatPercent: string): string {
  const net = Number(netMajor);
  const vat = Number(vatPercent);
  if (netMajor.trim() === "" || !Number.isFinite(net) || !Number.isFinite(vat)) {
    return "";
  }
  return (net * (1 + vat / 100)).toFixed(2);
}

function netFromGross(grossMajor: string, vatPercent: string): string {
  const gross = Number(grossMajor);
  const vat = Number(vatPercent);
  if (
    grossMajor.trim() === "" ||
    !Number.isFinite(gross) ||
    !Number.isFinite(vat) ||
    vat <= -100
  ) {
    return "";
  }
  return (gross / (1 + vat / 100)).toFixed(2);
}

function statusVariant(status: string): "success" | "muted" | "destructive" | "secondary" {
  if (["active", "running", "healthy", "ready"].includes(status)) return "success";
  if (["disabled", "deleting", "canceled"].includes(status)) return "muted";
  if (["failed", "error", "past_due"].includes(status)) return "destructive";
  return "secondary";
}

function configToForm(c: BillingConfig | null): BillingFormState {
  if (!c) {
    return {
      amountMajor: "",
      grossMajor: "",
      currency: "EUR",
      interval: "1 month",
      vatRatePercent: "0",
      description: "",
      notes: "",
      isActive: true,
      aiBillingMode: "credits",
      monthlyAllowanceCredits: "0",
      collectionMethod: "charge_automatically",
      invoiceDueDays: "14",
    };
  }
  const netMajor = (c.amountCents / 100).toFixed(2);
  const vatPercent = (c.vatRateBps / 100).toFixed(2);
  return {
    amountMajor: netMajor,
    grossMajor: grossFromNet(netMajor, vatPercent),
    currency: c.currency,
    interval: c.interval,
    vatRatePercent: vatPercent,
    description: c.description,
    notes: c.notes ?? "",
    isActive: c.isActive,
    aiBillingMode: c.aiBillingMode ?? "credits",
    monthlyAllowanceCredits: String(c.monthlyAllowanceCredits ?? 0),
    collectionMethod: c.collectionMethod ?? "charge_automatically",
    invoiceDueDays: String(c.invoiceDueDays ?? 14),
  };
}

function formToPayload(f: BillingFormState): UpsertBillingConfigRequest | BillingFormError {
  const amount = Number(f.amountMajor);
  if (!Number.isFinite(amount) || amount < 0) return "detail.billingConfig.errors.amount";
  const vat = Number(f.vatRatePercent);
  if (!Number.isFinite(vat) || vat < 0 || vat > 100) return "detail.billingConfig.errors.vat";
  if (!f.description.trim()) return "detail.billingConfig.errors.descriptionRequired";
  const allowance = Number(f.monthlyAllowanceCredits || "0");
  if (!Number.isInteger(allowance) || allowance < 0) {
    return "detail.billingConfig.errors.allowance";
  }
  const dueDays = Number(f.invoiceDueDays || "14");
  if (!Number.isInteger(dueDays) || dueDays < 1 || dueDays > 90) {
    return "detail.billingConfig.errors.paymentTerm";
  }
  return {
    amountCents: Math.round(amount * 100),
    currency: f.currency.toUpperCase(),
    interval: f.interval,
    vatRateBps: Math.round(vat * 100),
    description: f.description.trim(),
    notes: f.notes.trim() ? f.notes.trim() : null,
    isActive: f.isActive,
    aiBillingMode: f.aiBillingMode,
    monthlyAllowanceCredits: allowance,
    collectionMethod: f.collectionMethod,
    invoiceDueDays: dueDays,
  };
}

function isNoLlmKeysError(err: unknown): err is ApiError & { body: NoInstanceLlmKeysError } {
  return (
    err instanceof ApiError &&
    err.status === 422 &&
    !!err.body &&
    typeof err.body === "object" &&
    (err.body as { code?: unknown }).code === "NO_INSTANCE_LLM_KEYS"
  );
}

export function InstanceDetailPage() {
  const { t } = useTranslation("instances");
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["instance", name],
    queryFn: () => api.get<InstanceDetail>(`/api/admin/instances/${name}/billing-config`),
    enabled: !!name,
  });

  const [form, setForm] = React.useState<BillingFormState>(configToForm(null));
  const [initialized, setInitialized] = React.useState(false);

  // Net/VAT/Gross stay in sync: net is canonical, gross is derived. Editing net
  // or VAT recomputes gross; editing gross back-computes net.
  const handleNetChange = (netMajor: string) =>
    setForm((f) => ({
      ...f,
      amountMajor: netMajor,
      grossMajor: grossFromNet(netMajor, f.vatRatePercent),
    }));
  const handleGrossChange = (grossMajor: string) =>
    setForm((f) => ({
      ...f,
      grossMajor,
      amountMajor: netFromGross(grossMajor, f.vatRatePercent),
    }));
  const handleVatChange = (vatRatePercent: string) =>
    setForm((f) => ({
      ...f,
      vatRatePercent,
      grossMajor: grossFromNet(f.amountMajor, vatRatePercent),
    }));

  React.useEffect(() => {
    if (data && !initialized) {
      setForm(configToForm(data.config));
      setInitialized(true);
    }
  }, [data, initialized]);

  const [llmKeysWarning, setLlmKeysWarning] = React.useState<NoInstanceLlmKeysError | null>(null);
  // A VAT change refused because a subscription is running: asked again in-app.
  const [vatChangeConfirm, setVatChangeConfirm] = React.useState<{
    payload: UpsertBillingConfigRequest;
    message: string;
  } | null>(null);

  const saveMutation = useMutation({
    mutationFn: (payload: UpsertBillingConfigRequest) =>
      api.put<{ config: BillingConfig; customerTax?: { footerBlocked?: boolean } | null }>(
        `/api/admin/instances/${name}/billing-config`,
        payload
      ),
    onSuccess: (res) => {
      setVatChangeConfirm(null);
      if (res.customerTax?.footerBlocked) toast.warning(t("detail.billingConfig.legalNoteBlocked"));
      else toast.success(t("detail.billingConfig.saved"));
      setLlmKeysWarning(null);
      setForm(configToForm(res.config));
      qc.invalidateQueries({ queryKey: ["instance", name] });
    },
    onError: (err: unknown, payload) => {
      if (isNoLlmKeysError(err)) {
        setLlmKeysWarning(err.body);
        toast.error(t("detail.billingConfig.noLlmKeysToast"));
        return;
      }
      if (errorCode(err) === "VAT_CHANGE_SUBSCRIPTION_LIVE" && !payload.confirmVatChange) {
        setVatChangeConfirm({ payload, message: err instanceof Error ? err.message : "" });
        return;
      }
      toast.error(err instanceof Error ? err.message : t("detail.billingConfig.saveFailed"));
    },
  });

  const lifecycleMutation = useMutation({
    mutationFn: (action: "enable" | "disable") =>
      api.post<{ instance: { status: string } }>(
        `/api/admin/instances/${name}/${action}`
      ),
    onSuccess: (_res, action) => {
      toast.success(action === "enable" ? t("detail.lifecycle.enabled") : t("detail.lifecycle.disabled"));
      qc.invalidateQueries({ queryKey: ["instance", name] });
      qc.invalidateQueries({ queryKey: ["instances"] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t("detail.lifecycle.updateFailed"));
    },
  });

  const deleteMutation = useMutation({
    // confirmName is checked again on the server. The typed-name dialog below
    // is UX; the API refuses a DELETE that does not carry the name, so a bare
    // curl cannot skip the confirmation.
    mutationFn: () =>
      api.del<{ message: string }>(`/api/admin/instances/${name}`, { confirmName: name }),
    onSuccess: () => {
      toast.success(t("detail.lifecycle.deletionTriggered"));
      qc.invalidateQueries({ queryKey: ["instances"] });
      navigate("/");
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t("detail.lifecycle.deleteFailed"));
    },
  });

  function handleSave() {
    const payload = formToPayload(form);
    if (typeof payload === "string") {
      toast.error(t(payload));
      return;
    }
    saveMutation.mutate(payload);
  }

  if (isLoading || !initialized) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4">
        <Button variant="ghost" asChild>
          <Link to="/">
            <ArrowLeft className="mr-2 h-4 w-4" /> {t("detail.backToInstances")}
          </Link>
        </Button>
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-sm text-destructive">
              {notFound
                ? t("detail.notFound", { name })
                : t("detail.loadFailed", { error: error instanceof Error ? error.message : String(error) })}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const { instance, subscription, lastPayment } = data;
  const isDisabled = instance.status === "disabled";
  const scheduledDeletionAt = instance.scheduledDeletionAt
    ? new Date(instance.scheduledDeletionAt)
    : null;
  const deletionOverdue =
    isDisabled &&
    scheduledDeletionAt !== null &&
    scheduledDeletionAt.getTime() < Date.now();

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
          <Link to="/">
            <ArrowLeft className="mr-2 h-4 w-4" /> {t("detail.allInstances")}
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">{instance.name}</h1>
          <Badge variant={statusVariant(instance.status)}>{instance.status}</Badge>
          {deletionOverdue ? (
            <Badge variant="destructive">
              {t("detail.deletionOverdue", { date: formatDateTime(instance.scheduledDeletionAt) })}
            </Badge>
          ) : null}
        </div>
        <p className="text-sm text-muted-foreground">
          <a
            href={`https://${instance.domain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            {instance.domain}
          </a>
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("detail.billingConfig.title")}</CardTitle>
          <CardDescription>
            {t("detail.billingConfig.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="bc-amount">{t("detail.billingConfig.netAmount")}</Label>
              <Input
                id="bc-amount"
                inputMode="decimal"
                value={form.amountMajor}
                onChange={(e) => handleNetChange(e.target.value)}
                placeholder="29.00"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bc-vat">{t("detail.billingConfig.vat")}</Label>
              <Input
                id="bc-vat"
                inputMode="decimal"
                value={form.vatRatePercent}
                onChange={(e) => handleVatChange(e.target.value)}
                placeholder="19"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bc-gross">{t("detail.billingConfig.gross")}</Label>
              <Input
                id="bc-gross"
                inputMode="decimal"
                value={form.grossMajor}
                onChange={(e) => handleGrossChange(e.target.value)}
                placeholder="34.51"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="bc-currency">{t("detail.billingConfig.currency")}</Label>
              <Input
                id="bc-currency"
                maxLength={3}
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bc-interval">{t("detail.billingConfig.interval")}</Label>
              <Select
                value={form.interval}
                onValueChange={(v) => setForm({ ...form, interval: v as BillingInterval })}
              >
                <SelectTrigger id="bc-interval">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERVALS.map((i) => (
                    <SelectItem key={i} value={i}>
                      {t(INTERVAL_LABEL_KEYS[i])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bc-description">{t("detail.billingConfig.descriptionLabel")}</Label>
              <Input
                id="bc-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="NBTS Admin — Standard"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="bc-ai-billing">{t("detail.billingConfig.aiBilling")}</Label>
              <Select
                value={form.aiBillingMode}
                onValueChange={(v) => setForm({ ...form, aiBillingMode: v as AiBillingMode })}
              >
                <SelectTrigger id="bc-ai-billing">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="credits">{t("detail.billingConfig.aiBillingCredits")}</SelectItem>
                  <SelectItem value="byo_key">{t("detail.billingConfig.aiBillingByoKey")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bc-allowance">{t("detail.billingConfig.allowance")}</Label>
              <Input
                id="bc-allowance"
                inputMode="numeric"
                value={form.monthlyAllowanceCredits}
                onChange={(e) => setForm({ ...form, monthlyAllowanceCredits: e.target.value })}
                disabled={form.aiBillingMode === "byo_key"}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">
                {form.aiBillingMode === "byo_key"
                  ? t("detail.billingConfig.allowanceHintByoKey")
                  : t("detail.billingConfig.allowanceHintCredits")}
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="bc-collection">{t("detail.billingConfig.collection")}</Label>
              <Select
                value={form.collectionMethod}
                onValueChange={(v) =>
                  setForm({ ...form, collectionMethod: v as CollectionMethod })
                }
              >
                <SelectTrigger id="bc-collection">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="charge_automatically">
                    {t("detail.billingConfig.collectionAuto")}
                  </SelectItem>
                  <SelectItem value="send_invoice">
                    {t("detail.billingConfig.collectionInvoice")}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {form.collectionMethod === "send_invoice"
                  ? t("detail.billingConfig.collectionHintInvoice")
                  : t("detail.billingConfig.collectionHintAuto")}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bc-due-days">{t("detail.billingConfig.paymentTerm")}</Label>
              <Input
                id="bc-due-days"
                inputMode="numeric"
                value={form.invoiceDueDays}
                onChange={(e) => setForm({ ...form, invoiceDueDays: e.target.value })}
                disabled={form.collectionMethod !== "send_invoice"}
                placeholder="14"
              />
              <p className="text-xs text-muted-foreground">
                {form.collectionMethod === "send_invoice"
                  ? t("detail.billingConfig.paymentTermHintInvoice")
                  : t("detail.billingConfig.paymentTermHintAuto")}
              </p>
            </div>
          </div>

          {llmKeysWarning ? (
            <div
              role="alert"
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
            >
              <p className="font-medium">{t("detail.billingConfig.noLlmKeysTitle")}</p>
              <p className="mt-1">{llmKeysWarning.error}</p>
              {llmKeysWarning.keyHints.length > 0 ? (
                <p className="mt-1 font-mono text-xs">
                  {t("detail.billingConfig.knownKeys", { keys: llmKeysWarning.keyHints.join(", ") })}
                </p>
              ) : (
                <p className="mt-1 text-xs">
                  {t("detail.billingConfig.addKeyHint")}
                </p>
              )}
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="bc-notes">{t("detail.billingConfig.notes")}</Label>
            <Textarea
              id="bc-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder={t("detail.billingConfig.notesPlaceholder")}
            />
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="bc-active"
              checked={form.isActive}
              onCheckedChange={(v) => setForm({ ...form, isActive: v })}
            />
            <Label htmlFor="bc-active" className="cursor-pointer">
              {t("detail.billingConfig.active")}
            </Label>
          </div>

          <div className="flex justify-end">
            <Dialog
              open={vatChangeConfirm !== null}
              onOpenChange={(v) => !saveMutation.isPending && !v && setVatChangeConfirm(null)}
            >
              <DialogContent closeLabel={t("detail.cancel")}>
                <DialogHeader>
                  <DialogTitle>{t("detail.billingConfig.vatChangeTitle")}</DialogTitle>
                  <DialogDescription>{vatChangeConfirm?.message}</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setVatChangeConfirm(null)} disabled={saveMutation.isPending}>
                    {t("detail.cancel")}
                  </Button>
                  <Button
                    variant="destructive"
                    disabled={saveMutation.isPending}
                    onClick={() =>
                      vatChangeConfirm &&
                      saveMutation.mutate({ ...vatChangeConfirm.payload, confirmVatChange: true })
                    }
                  >
                    {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t("detail.billingConfig.vatChangeConfirm")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("detail.billingConfig.save")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <StripeCustomerCard
        name={instance.name}
        subscription={subscription}
        vatCheck={instance.vatCheck ?? null}
        vatConfirmations={instance.vatConfirmations ?? []}
        partner={instance.partner ?? null}
      />

      <InstanceBillingOperationsSection
        name={instance.name}
        vatRatePercent={Number(form.vatRatePercent) || 0}
        currency={form.currency || "EUR"}
        subscription={subscription}
        config={data.config}
        internal={Boolean(instance.internal)}
      />

      <CreditGrantsSection name={instance.name} />

      <Card>
        <CardHeader>
          <CardTitle>{t("detail.subscription.title")}</CardTitle>
          <CardDescription>
            {t("detail.subscription.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div>
            <h3 className="text-sm font-medium">{t("detail.subscription.subscription")}</h3>
            {subscription ? (
              <dl className="mt-2 space-y-1 text-sm">
                <KV k={t("detail.subscription.status")} v={subscription.status} />
                <StripeLinkKV
                  k={t("detail.subscription.stripeSubscription")}
                  id={subscription.stripeSubscriptionId}
                  href={`https://dashboard.stripe.com/subscriptions/${subscription.stripeSubscriptionId}`}
                />
                <StripeLinkKV
                  k={t("detail.subscription.stripeCustomer")}
                  id={subscription.stripeCustomerId}
                  href={`https://dashboard.stripe.com/customers/${subscription.stripeCustomerId}`}
                />
                {subscription.stripeCustomerId ? <StripeCustomerEmailKV name={instance.name} /> : null}
                <KV k={t("detail.subscription.started")} v={formatDateTime(subscription.startedAt)} />
                <KV k={t("detail.subscription.nextPayment")} v={formatDateTime(subscription.nextPaymentAt)} />
                <KV k={t("detail.subscription.periodEnd")} v={formatDateTime(subscription.currentPeriodEnd)} />
                <KV k={t("detail.subscription.graceEnd")} v={formatDateTime(subscription.gracePeriodEndsAt)} />
              </dl>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">{t("detail.subscription.noSubscription")}</p>
            )}
          </div>
          <div>
            <h3 className="text-sm font-medium">{t("detail.subscription.lastPayment")}</h3>
            {lastPayment ? (
              <dl className="mt-2 space-y-1 text-sm">
                <KV k={t("detail.subscription.status")} v={lastPayment.status} />
                <KV
                  k={t("detail.subscription.amount")}
                  v={`${(lastPayment.amountCents / 100).toFixed(2)} ${lastPayment.currency}`}
                />
                <KV k={t("detail.subscription.paid")} v={formatDateTime(lastPayment.paidAt)} />
                {lastPayment.invoicePdfUrl ? (
                  <div className="flex gap-2">
                    <dt className="w-28 text-muted-foreground">{t("detail.subscription.invoice")}</dt>
                    <dd>
                      <a
                        className="text-primary hover:underline"
                        href={lastPayment.invoicePdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        PDF
                      </a>
                    </dd>
                  </div>
                ) : null}
                {lastPayment.failureReason ? (
                  <KV k={t("detail.subscription.failure")} v={lastPayment.failureReason} />
                ) : null}
              </dl>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">{t("detail.subscription.noPayments")}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <PaymentsSection name={instance.name} />

      <InstanceBillingAuditCard name={instance.name} />

      <ProvisioningStepsCard instance={instance} />

      <EmployeeManagementSection name={instance.name} />

      <InstanceAiSettingsSection name={instance.name} />

      <InstanceEmployeesSection name={instance.name} />

      <InstanceLlmUsageSection name={instance.name} />

      <InstanceVoiceUsageSection name={instance.name} />

      <Card>
        <CardHeader>
          <CardTitle>{t("detail.lifecycle.title")}</CardTitle>
          <CardDescription>
            {t("detail.lifecycle.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            {isDisabled ? (
              <Button
                variant="secondary"
                onClick={() => lifecycleMutation.mutate("enable")}
                disabled={lifecycleMutation.isPending}
              >
                {t("detail.lifecycle.enable")}
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={() => lifecycleMutation.mutate("disable")}
                disabled={lifecycleMutation.isPending}
              >
                {t("detail.lifecycle.disable")}
              </Button>
            )}
            {instance.scheduledDeletionAt ? (
              deletionOverdue ? (
                <Badge variant="destructive">
                  {t("detail.deletionOverdue", { date: formatDateTime(instance.scheduledDeletionAt) })}
                </Badge>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {t("detail.lifecycle.scheduledDeletion", { date: formatDateTime(instance.scheduledDeletionAt) })}
                </span>
              )
            ) : null}
          </div>

          <Separator />

          <DeleteInstanceSection
            name={instance.name}
            pending={deleteMutation.isPending}
            onConfirm={() => deleteMutation.mutate()}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function EmployeeManagementSection({ name }: { name: string }) {
  const { t } = useTranslation("instances");
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["instance", name, "employee-management"],
    queryFn: () =>
      api.get<{ mode: "local" | "central" }>(
        `/api/admin/instances/${name}/employee-management`
      ),
  });

  const mutation = useMutation({
    mutationFn: (mode: "local" | "central") =>
      api.put<{ mode: "local" | "central" }>(
        `/api/admin/instances/${name}/employee-management`,
        { mode }
      ),
    onSuccess: (res) => {
      toast.success(
        res.mode === "central"
          ? t("detail.employeeManagement.nowCentral")
          : t("detail.employeeManagement.nowLocal")
      );
      qc.invalidateQueries({ queryKey: ["instance", name, "employee-management"] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t("detail.employeeManagement.updateFailed"));
    },
  });

  const central = data?.mode === "central";

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("detail.employeeManagement.title")}</CardTitle>
        <CardDescription>
          {t("detail.employeeManagement.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3">
          <Switch
            id="em-central"
            checked={central}
            disabled={isLoading || mutation.isPending}
            onCheckedChange={(v) => mutation.mutate(v ? "central" : "local")}
          />
          <Label htmlFor="em-central" className="cursor-pointer">
            {t("detail.employeeManagement.managedCentrally")}
          </Label>
          {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          <Badge variant={central ? "success" : "secondary"}>
            {central ? "central" : "local"}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}

function paymentStatusVariant(
  status: string
): "success" | "muted" | "destructive" | "secondary" {
  if (["paid", "succeeded"].includes(status)) return "success";
  if (["failed", "uncollectible"].includes(status)) return "destructive";
  if (["canceled", "void", "refunded"].includes(status)) return "muted";
  return "secondary";
}

/**
 * Invoices the operator can still act on: real Stripe invoices (not top-up
 * Checkout rows) that are unpaid — 'failed' is an open invoice whose last
 * charge was declined.
 */
function isActionableInvoice(p: InstancePayment): boolean {
  return p.stripeInvoiceId.startsWith("in_") && (p.status === "open" || p.status === "failed");
}

/** Paid Stripe invoices that still have something left to credit. */
function isCreditableInvoice(p: InstancePayment): boolean {
  const gross = p.amountCents;
  const returned = Math.max(p.creditedCents ?? 0, p.refundedCents ?? 0);
  return p.stripeInvoiceId.startsWith("in_") && p.status === "paid" && returned < gross;
}

function PaymentsSection({ name }: { name: string }) {
  const { t } = useTranslation("instances");
  const { t: tb } = useTranslation("billing");
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["instance", name, "payments"],
      queryFn: ({ pageParam }) =>
        api.get<PaymentsListResponse>(
          `/api/admin/instances/${name}/payments?limit=20${
            pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ""
          }`
        ),
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    });

  const payments = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("detail.payments.title")}</CardTitle>
        <CardDescription>
          {t("detail.payments.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : error ? (
          <p className="text-sm text-destructive">
            {t("detail.loadFailed", { error: error instanceof Error ? error.message : String(error) })}
          </p>
        ) : payments.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("detail.payments.empty")}</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">{t("detail.payments.columns.date")}</th>
                    <th className="py-2 pr-4 font-medium">{t("detail.payments.columns.description")}</th>
                    <th className="py-2 pr-4 font-medium">{t("detail.payments.columns.period")}</th>
                    <th className="py-2 pr-4 font-medium">{t("detail.payments.columns.amount")}</th>
                    <th className="py-2 pr-4 font-medium">{t("detail.payments.columns.status")}</th>
                    <th className="py-2 pr-4 font-medium">{t("detail.payments.columns.links")}</th>
                    <th className="py-2 font-medium">{tb("ops.actionsColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-b align-top last:border-0">
                      <td className="whitespace-nowrap py-2 pr-4 text-muted-foreground">
                        {formatDateTime(p.paidAt ?? p.createdAt)}
                      </td>
                      <td className="py-2 pr-4">{p.description ?? p.sequenceType ?? "—"}</td>
                      <td className="whitespace-nowrap py-2 pr-4 text-muted-foreground">
                        {formatPeriod(p.periodStart, p.periodEnd)}
                      </td>
                      <td className="whitespace-nowrap py-2 pr-4 font-medium tabular-nums">
                        {(p.amountCents / 100).toFixed(2)} {p.currency}
                        {p.creditedCents ? (
                          <span className="block text-xs font-normal text-muted-foreground">
                            {tb("payments.credited", { amount: formatMoney(p.creditedCents, p.currency) })}
                          </span>
                        ) : null}
                        {p.refundedCents ? (
                          <span className="block text-xs font-normal text-muted-foreground">
                            {tb("payments.refunded", { amount: formatMoney(p.refundedCents, p.currency) })}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge variant={paymentStatusVariant(p.status)}>{p.status}</Badge>
                        {p.failureReason ? (
                          <span
                            className="mt-1 block max-w-[16rem] truncate text-xs text-destructive"
                            title={p.failureReason}
                          >
                            {p.failureReason}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex gap-3">
                          {p.invoicePdfUrl ? (
                            <a
                              className="text-primary hover:underline"
                              href={p.invoicePdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t("detail.payments.invoicePdf")}
                            </a>
                          ) : null}
                          {p.hostedInvoiceUrl ? (
                            <a
                              className="text-primary hover:underline"
                              href={p.hostedInvoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Stripe
                            </a>
                          ) : null}
                          {p.creditNotePdfUrl ? (
                            <a
                              className="text-primary hover:underline"
                              href={p.creditNotePdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {tb("payments.creditNotePdf")}
                            </a>
                          ) : null}
                          {!p.invoicePdfUrl && !p.hostedInvoiceUrl && !p.creditNotePdfUrl ? (
                            <span className="text-muted-foreground">—</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="py-2">
                        {isActionableInvoice(p) ? (
                          <InvoiceRowActions
                            stripeInvoiceId={p.stripeInvoiceId}
                            instanceName={name}
                            label={p.description ?? p.stripeInvoiceId}
                            lastReminderAt={p.lastReminderAt}
                          />
                        ) : isCreditableInvoice(p) ? (
                          <div className="flex flex-col items-start gap-1">
                            <CreditNoteAction
                              stripeInvoiceId={p.stripeInvoiceId}
                              instanceName={name}
                              label={p.description ?? p.stripeInvoiceId}
                            />
                            {p.paidOutOfBand ? (
                              <span className="text-xs text-muted-foreground">
                                {tb("payments.outOfBand")}
                              </span>
                            ) : null}
                          </div>
                        ) : p.paidOutOfBand ? (
                          <span className="text-xs text-muted-foreground">
                            {tb("payments.outOfBand")}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasNextPage ? (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t("detail.payments.loadMore")}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function CreditGrantsSection({ name }: { name: string }) {
  const qc = useQueryClient();
  const { t } = useTranslation("instances");
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["instance", name, "credit-grants"],
    queryFn: () =>
      api.get<{ grants: CreditGrant[] }>(`/api/admin/billing/instances/${name}/credit-grants`),
  });

  const grantMutation = useMutation({
    mutationFn: (payload: { credits: number; note?: string }) =>
      api.post<{ grant: CreditGrant }>(
        `/api/admin/billing/instances/${name}/credit-grants`,
        payload
      ),
    onSuccess: (res) => {
      toast.success(t("detail.creditGrants.granted", { count: res.grant.credits }));
      setDialogOpen(false);
      qc.invalidateQueries({ queryKey: ["instance", name, "credit-grants"] });
      qc.invalidateQueries({ queryKey: ["instance", name, "billing-audit"] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t("detail.creditGrants.grantFailed"));
    },
  });

  const grants = data?.grants ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>{t("detail.creditGrants.title")}</CardTitle>
            <CardDescription>
              {t("detail.creditGrants.description")}
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
            {t("detail.creditGrants.grant")}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : error ? (
          <p className="text-sm text-destructive">
            {t("detail.loadFailed", { error: error instanceof Error ? error.message : String(error) })}
          </p>
        ) : grants.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("detail.creditGrants.empty")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">{t("detail.creditGrants.columns.kind")}</th>
                  <th className="py-2 pr-4 font-medium">{t("detail.creditGrants.columns.credits")}</th>
                  <th className="py-2 pr-4 font-medium">{t("detail.creditGrants.columns.created")}</th>
                  <th className="py-2 pr-4 font-medium">{t("detail.creditGrants.columns.applied")}</th>
                  <th className="py-2 font-medium">{t("detail.creditGrants.columns.note")}</th>
                </tr>
              </thead>
              <tbody>
                {grants.map((g) => (
                  <tr key={g.id} className="border-b last:border-0">
                    <td className="py-2 pr-4">
                      <Badge variant={g.kind === "manual" ? "secondary" : "muted"}>{g.kind}</Badge>
                    </td>
                    <td className="py-2 pr-4 font-medium">{g.credits.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {formatDateTime(g.createdAt)}
                    </td>
                    <td className="py-2 pr-4">
                      {g.appliedAt ? (
                        <span className="text-muted-foreground">{formatDateTime(g.appliedAt)}</span>
                      ) : g.applyError ? (
                        <span className="text-destructive" title={g.applyError}>
                          {t("detail.creditGrants.applyFailed")}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">{t("detail.creditGrants.applyPending")}</span>
                      )}
                    </td>
                    <td className="py-2 text-muted-foreground">{g.note ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {dialogOpen ? (
        <GrantCreditsDialog
          name={name}
          pending={grantMutation.isPending}
          onClose={() => setDialogOpen(false)}
          onSubmit={(credits, note) =>
            grantMutation.mutate({ credits, ...(note ? { note } : {}) })
          }
        />
      ) : null}
    </Card>
  );
}

function GrantCreditsDialog({
  name,
  pending,
  onClose,
  onSubmit,
}: {
  name: string;
  pending: boolean;
  onClose: () => void;
  onSubmit: (credits: number, note: string) => void;
}) {
  const { t } = useTranslation("instances");
  const [credits, setCredits] = React.useState("");
  const [note, setNote] = React.useState("");

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const parsed = Number(credits);
  const valid = Number.isInteger(parsed) && parsed > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="grant-dialog-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border bg-background p-6 shadow-lg">
        <h2 id="grant-dialog-title" className="text-lg font-semibold">
          {t("detail.creditGrants.dialog.title", { name })}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("detail.creditGrants.dialog.description")}
        </p>
        <div className="mt-4 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="grant-credits-input">{t("detail.creditGrants.dialog.credits")}</Label>
            <Input
              id="grant-credits-input"
              autoFocus
              inputMode="numeric"
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              placeholder="1000"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="grant-note-input">{t("detail.creditGrants.dialog.note")}</Label>
            <Input
              id="grant-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("detail.creditGrants.dialog.notePlaceholder")}
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {t("detail.cancel")}
          </Button>
          <Button
            disabled={!valid || pending}
            onClick={() => {
              if (!valid) return;
              onSubmit(parsed, note.trim());
            }}
          >
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("detail.creditGrants.grant")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function KV({ k, v }: { k: string; v: string | null | undefined }) {
  return (
    <div className="flex gap-2">
      <dt className="w-28 text-muted-foreground">{k}</dt>
      <dd className="flex-1 break-all">{v ?? "—"}</dd>
    </div>
  );
}

function StripeLinkKV({
  k,
  id,
  href,
}: {
  k: string;
  id: string | null | undefined;
  href: string;
}) {
  return (
    <div className="flex gap-2">
      <dt className="w-28 text-muted-foreground">{k}</dt>
      <dd className="flex-1 break-all">
        {id ? (
          <a
            className="text-primary hover:underline"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {id}
          </a>
        ) : (
          "—"
        )}
      </dd>
    </div>
  );
}

function DeleteInstanceSection({
  name,
  pending,
  onConfirm,
}: {
  name: string;
  pending: boolean;
  onConfirm: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation("instances");
  const [typed, setTyped] = React.useState("");
  const matches = typed === name;

  React.useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <div>
        <Button variant="destructive" onClick={() => setOpen(true)} disabled={pending}>
          <Trash2 className="mr-2 h-4 w-4" />
          {t("detail.delete.button")}
        </Button>
        <p className="mt-2 text-xs text-muted-foreground">
          {t("detail.delete.hint")}
        </p>
      </div>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-dialog-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="w-full max-w-md rounded-lg border bg-background p-6 shadow-lg">
            <h2 id="delete-dialog-title" className="text-lg font-semibold">
              {t("detail.delete.title", { name })}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("detail.delete.warningBefore")}{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs font-mono">{name}</code>{" "}
              {t("detail.delete.warningAfter")}
            </p>
            <div className="mt-4 space-y-2">
              <Label htmlFor="delete-confirm-input">{t("detail.delete.nameLabel")}</Label>
              <Input
                id="delete-confirm-input"
                autoFocus
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={name}
              />
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)} disabled={pending}>
                {t("detail.cancel")}
              </Button>
              <Button
                variant="destructive"
                disabled={!matches || pending}
                onClick={() => {
                  if (!matches) return;
                  onConfirm();
                  setOpen(false);
                }}
              >
                {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("detail.delete.button")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
